// app/src/components/OnboardingTour.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { setTourFlag } from "../api/onboarding";

type Step = {
  route: string;              // ruta a la que ir antes de mostrar
  selector: string;           // CSS selector del ancla (data-tour=…)
  title: string;
  body: string;
  allowNextIfMissing?: boolean; // por si el nodo tarda o no existe
};

const STEPS: Step[] = [
  {
    route: "/product",
    selector: '[data-tour="tab-producto"]',
    title: "Tus productos",
    body: "Aquí verás los productos que tienes contratados y el histórico de tus compras.",
  },
  {
    route: "/product",
    selector: '[data-tour="products-tab-hire"]',
    title: "Contratar productos",
    body: "Aquí puedes contratar nuevos servicios. Recuerda que solo podrás contratar si tienes saldo suficiente en tu monedero o una tarjeta guardada.",
    // Entrar en la pestaña de contratar
    allowNextIfMissing: true,
  },
  {
    route: "/calendar",
    selector: '[data-tour="tab-calendario"]',
    title: "Reservar sesión",
    body: "Una vez contratado un producto, podrás reservar una sesión desde aquí. Solo tienes que clicar en el día deseado y elegir el producto para agendar tu cita.",
  },
  {
    route: "/user",
    selector: '[data-tour="tab-usuario"]',
    title: "Gestión de usuario",
    body: "En esta sección podrás gestionar tu perfil, ver tu monedero y acceder a la gestión de tus tarjetas.",
  },
  {
    route: "/user",
    selector: '[data-tour="user-favoritos"]',
    title: "Favoritos",
    body: "Aquí podrás seleccionar y consultar tus profesionales favoritos para acceder a ellos más fácilmente.",
  },
  {
    route: "/user",
    selector: '[data-tour="user-documentos"]',
    title: "Documentos",
    body: "En este apartado puedes subir documentos que ayuden a los profesionales a conocer mejor tu situación.",
  },
  {
    route: "/user",
    selector: '[data-tour="user-mis-tarjetas"]',
    title: "Tus tarjetas",
    body: "Haz clic aquí para acceder a la sección 'Mis tarjetas', donde podrás añadir una tarjeta para futuras compras o recargas.",
  },
  {
    route: "/payment-methods",
    selector: '[data-tour="user-tarjetas-add"]',
    title: "Añadir tarjeta",
    body: "Pulsa aquí para añadir una nueva tarjeta de pago de forma segura.",
  },
];

export default function OnboardingTour({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [i, setI] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const rafRef = useRef<number | null>(null);

  const step = STEPS[i];

  // Navega a la ruta del paso cuando cambie el índice
  useEffect(() => {
    if (pathname !== step.route) {
      navigate(step.route, { replace: true });
    } else if (i === 1) {
      // Forzar la pestaña de contratar productos
      const hireTab = document.querySelector('[data-tour="products-tab-hire"]') as HTMLElement | null;
      if (hireTab) hireTab.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Busca el elemento ancla y actualiza su bounding rect
  useEffect(() => {
    let cancelled = false;
    function tick() {
      const el = document.querySelector(step.selector) as HTMLElement | null;
      setAnchor(el || null);
      setRect(el ? el.getBoundingClientRect() : null);
      rafRef.current = requestAnimationFrame(tick);
    }
    tick();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [pathname, step.selector]);

  function next() {
    if (i < STEPS.length - 1) setI(i + 1);
    else finish();
  }
  function back() {
    if (i > 0) setI(i - 1);
  }
  async function finish() {
    await setTourFlag(true);
    onClose();
  }

  // Portal root
  const root = useMemo(() => {
    let node = document.getElementById("onboarding-root");
    if (!node) {
      node = document.createElement("div");
      node.id = "onboarding-root";
      document.body.appendChild(node);
    }
    return node;
  }, []);

  if (!root) return null;

  // Dimensiones para el “agujero” (spotlight)
  const hole = rect ?? new DOMRect(window.innerWidth / 2 - 120, window.innerHeight / 2 - 40, 240, 80);

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        pointerEvents: "auto",
      }}
      aria-modal
      role="dialog"
    >
      {/* Capa oscura con agujero */}
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <mask id="hole-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect x={hole.x - 8} y={hole.y - 8} width={hole.width + 16} height={hole.height + 16} rx="16" fill="black" />
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,.55)" mask="url(#hole-mask)" />
      </svg>

      {/* Popover */}
      <div
        style={{
          position: "absolute",
          left: Math.min(Math.max(hole.x, 16), window.innerWidth - 320),
          top: hole.y + hole.height + 12 + (hole.y > window.innerHeight - 220 ? - (hole.height + 180) : 0),
          width: 304,
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          padding: 14,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 6 }}>{step.title}</div>
        <div style={{ color: "#374151", fontSize: 14, lineHeight: 1.35 }}>{step.body}</div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "space-between" }}>
          <button onClick={onClose} style={btnGhost}>Saltar</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={back} disabled={i === 0} style={btnGhost}>Atrás</button>
            <button onClick={next} style={btnPrimary}>{i === STEPS.length - 1 ? "Entendido" : "Siguiente"}</button>
          </div>
        </div>
      </div>
    </div>,
    root
  );
}

const btnPrimary: React.CSSProperties = {
  background: "#111827",
  color: "#fff",
  border: "1px solid #111827",
  borderRadius: 10,
  padding: "8px 12px",
  fontWeight: 700,
  cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  background: "#fff",
  color: "#111827",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "8px 12px",
  cursor: "pointer",
};